import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BsCheck } from 'react-icons/bs';
import { IoClose } from 'react-icons/io5';
import { HiOutlinePencil } from 'react-icons/hi2';
import { DeviceType } from '@/utils/definitions';
import { HSpace } from '@/components/spacing';
import {
  EditorContext,
  EditorContextProps,
} from '@/features/editor/context/editor-context';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { ConfirmModal } from '@/components/confirm-modal';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { maybe } from '@/features/details/utils';
import { ExperienceVariant } from '@/webapi/use-experience-api';
import { InlineVariantsLayoutAllocationText } from '@/features/editor/widgets/shared/inline-variants-layout/shared';
import { CircularCheckmark } from '@/components/circular-checkmark';

export interface InlineVariantsLayoutVariantNameProps {
  device: DeviceType;
  defaultValue: string;
  variantId: string;
  bg?: string;
  _variants?: ExperienceVariant[];
  isActive: boolean;
  disableNameEdit?: boolean;
  disableRemove?: boolean;
  onRenameVariant?: (variantId: string, name: string) => void;
  footer?: React.ReactNode;
  baselineVariantId?: string | null;
  setBaselineVariantId?: (id: string | null) => void;
  updateEnabled?: boolean;
  chance?: number;
}

export function InlineVariantsLayoutVariantName({
  defaultValue,
  device,
  variantId,
  isActive,
  disableNameEdit,
  _variants,
  bg,
  disableRemove,
  onRenameVariant,
  footer,
  baselineVariantId,
  setBaselineVariantId,
  updateEnabled,
  chance,
}: InlineVariantsLayoutVariantNameProps) {
  const edtCtx = useContext(EditorContext);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);
  const [name, setName] = useState(defaultValue);
  const initialName = useRef<string>(defaultValue);

  useEffect(() => {
    if (!isEditing) {
      setName(defaultValue);
      initialName.current = defaultValue;
    }
  }, [defaultValue]);

  const handleEdit = () => {
    if (isEditing) {
      if (onRenameVariant) {
        onRenameVariant(variantId, name);
      } else {
        edtCtx.experienceState?.renameVariant(variantId, name);
      }
      initialName.current = name;
      setHasChanged(false);
    }
    setIsEditing(!isEditing);
  };

  const handleCancel = () => {
    setName(initialName.current);
    setHasChanged(false);
    setIsEditing(false);
  };
  const isMultiVariantEnabled = useFeatureBit(FeatureBit.MULTIVARIANT);
  if (!isMultiVariantEnabled) {
    return (
      <InlineVariantsLayoutAllocationText device={device}>
        {defaultValue}
      </InlineVariantsLayoutAllocationText>
    );
  }
  const changes = countChanges(edtCtx, variantId, _variants);

  const updateWidth = (
    e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
  ) => {
    const el = e.currentTarget;
    el.style.width = `0px`;
    el.style.width = `${el.scrollWidth}px`;
    setInputWidth(el.scrollWidth);
  };
  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateWidth(e);
    const newName = e.currentTarget.value;
    setName(newName);
    setHasChanged(newName !== initialName.current);
  };

  return (
    <Wrapper className="variant-name-wrapper">
      {isEditing ? (
        <FlexRow>
          <NameInput
            autoFocus
            value={name}
            style={{ width: inputWidth }}
            onChange={onNameChange}
            onFocus={updateWidth}
            maxLength={20}
            className="variant-name-input"
          />
          <HSpace value={1.5} />
          <ButtonWrapper color="#2DD892">
            <BsCheck
              onClick={() => (hasChanged ? handleEdit() : setIsEditing(false))}
              color="white"
              size={20}
            />
          </ButtonWrapper>
          <HSpace value={0.5} />
          <ButtonWrapper>
            <IoClose onClick={handleCancel} color="white" size={16} />
          </ButtonWrapper>
        </FlexRow>
      ) : (
        <>
          {updateEnabled && (
            <>
              <CircularCheckmark
                onChange={(selected) =>
                  setBaselineVariantId?.(selected ? variantId : null)
                }
                disabled={!(chance > 0)}
                selected={variantId === baselineVariantId}
                fullWidth
              />
              <HSpace value={1} />
            </>
          )}
          <FlexCol isActive={isActive}>
            <NameContainer>
              <TextSpan>{defaultValue}</TextSpan>
              {!disableNameEdit && !updateEnabled && (
                <HiOutlinePencil size={15} onClick={handleEdit} />
              )}
            </NameContainer>
            {!disableRemove &&
              (footer || <ChangesSpan>{changes} Changes</ChangesSpan>)}
          </FlexCol>

          {!disableRemove && (
            <Actions
              isActive={isActive}
              bg={bg}
              handleDelete={() =>
                edtCtx.experienceState.deleteVariant(variantId)
              }
              editNow={() =>
                edtCtx.experienceState.selectActiveVariant(variantId)
              }
            />
          )}
        </>
      )}
    </Wrapper>
  );
}

const NameContainer = styled.div`
  display: flex;
  align-items: center;

  span {
    margin-right: 0.5rem;
  }

  && svg {
    cursor: pointer;
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(p: { color?: string }) => p.color || `#CEDCD7`};
  border-radius: 50%;
  cursor: pointer;
  height: 20px;
  width: 20px;

  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }
`;
const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  color: ${(p: { isActive: boolean }) => (p.isActive ? `#3451fa` : `#5b656e`)};
  width: 100%;
`;

const TextSpan = styled.span`
  font-family: 'Inter', serif;
  font-size: 1.4rem;
  font-weight: 600;
  overflow-wrap: break-word;
  max-width: 160px;
  text-align: left;
  overflow: hidden;
`;

const FlexRow = styled.div`
  display: flex;
`;
const ChangesSpan = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 60px;
  justify-content: center;
  margin-top: 0.5rem;

  font-family: Inter, serif;
`;

const Wrapper = styled.div`
  padding: 0.5rem 0 0.5rem 2rem;
  display: flex;
  position: relative;
  width: 85%;
  &&:has(.checkmark-content) {
    padding-left: 0.5rem;
  }
`;

const NameInput = styled.input`
  font-family: 'Inter', serif;
  font-size: 1.4rem;
  font-weight: 600;
  color: black;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  max-width: 160px;
`;

function Actions({
  editNow,
  handleDelete,
  isActive,
  bg,
}: {
  handleDelete: () => void;
  editNow: (id: string) => void;
  isActive: boolean;
  bg: string;
}) {
  const edtCtx = useContext(EditorContext);
  const isMultiVariant =
    edtCtx?.experienceState?.currentExperience?.variants?.length > 1;

  const ref = useRef();
  const { props, show, hide } = sharedOverlayProps(ref);
  if (!isMultiVariant) {
    return null;
  }

  return (
    <EditContainer>
      <ActionsContainer>
        <HSpace value={1} />
        <SharedElementOverlay {...props}>
          <ConfirmModal
            onDiscard={hide}
            onConfirm={handleDelete}
            title="Are you sure?"
            description="Are you sure you want to delete this variation?"
            yesCaption="Yes"
            noCaption="No"
          />
        </SharedElementOverlay>
        <BtnWrapper bg={bg} onClick={show} className="variant-remove-btn">
          <span ref={ref}>Remove</span>
        </BtnWrapper>
        <HSpace value={2} />
        <BtnWrapper
          bg={bg}
          onClick={editNow}
          isActive={isActive}
          className="variant-edit-btn"
        >
          <span>{isActive ? `Editing` : `Edit`}</span>
        </BtnWrapper>
        <HSpace />
      </ActionsContainer>
      <HSpace />
    </EditContainer>
  );
}

const ActionsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
`;

const EditContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${(p: { isEditing: boolean }) =>
    p.isEditing ? `start` : `space-between`};
`;

const BtnWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  opacity: ${(p: { isActive: boolean }) => (p.isActive ? `0.5` : `1`)};
  color: #9199a6;

  span {
    text-decoration: underline;
  }

  font-family: Inter, serif;
  font-size: ${(p) => (p.bg ? `1.25` : `1`)}rem;

  :hover {
    opacity: ${(p: { isActive: boolean }) => (p.isActive ? `0.5` : `0.6`)};
    cursor: ${(p: { isActive: boolean }) =>
      p.isActive ? `default` : `pointer`};
  }

  :active {
    opacity: ${(p: { isActive: boolean }) => (p.isActive ? `0.5` : `1`)};
    cursor: default;
  }
`;

function countChanges(
  edtCtx: EditorContextProps,
  variantId: string,
  _variants: ExperienceVariant[],
) {
  return maybe(
    () =>
      (_variants || edtCtx.experienceState.currentExperience.variants).find(
        (v) => v.id === variantId,
      ).changes.length,
    0,
  );
}

function sharedOverlayProps(ref: React.MutableRefObject<undefined>) {
  const { props, show, hide } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    ref,
    () => centered(18, 42),
  );
  return { props, show, hide };
}
